import { useCallback, useMemo, useState } from 'react'

import { getMaxDateTimeForOrder } from 'utils/time'
import moment from 'moment-timezone'

export function useDateAndTime(orderDateTime?: Date) {

  const [dateTime, setDateTime] = useState(orderDateTime)
  const [date, setDate] = useState(orderDateTime)
  const [time, setTime] = useState(orderDateTime)

  /**
   * Last day an order or assignment may be scheduled for.
   * Pass it to the picker as maxDate so the calendar is bounded too (QA-361).
   */
  const maxDateTime = useMemo(() => getMaxDateTimeForOrder(), [])

  const onDateChange = useCallback((newDate: Date) => {
    if (!newDate) return

    const newDateTime = new Date(dateTime || new Date())
    // In one call: setting the day first would roll a 31st over to the 1st whenever the date already set is in a shorter month.
    newDateTime.setFullYear(newDate.getFullYear(), newDate.getMonth(), newDate.getDate())

    // The pickers allow free-text typing, so an absurd typed year (e.g. 9202) reaches this callback
    // even with maxDate set on the picker. Refuse it here rather than letting it reach the API.
    // Compared by calendar day, the same granularity the picker's own maxDate uses, so the last
    // allowed day stays fully selectable; the backend guard is a day looser for exactly that reason.
    // Only the date carries the year, so the time callback below needs no such guard.
    if (moment(newDateTime).isAfter(maxDateTime, 'day')) return

    setDate(newDate)
    setDateTime(newDateTime)
  }, [dateTime, maxDateTime])

  const onTimeChange = useCallback((newTime: Date) => {
    if (!newTime) return

    const newDateTime = new Date(dateTime || new Date())
    newDateTime.setHours(newTime.getHours())
    newDateTime.setMinutes(newTime.getMinutes())

    setTime(newTime)
    setDateTime(newDateTime)
  }, [dateTime])

  return {
    date,
    time,
    dateTime,
    maxDateTime,
    onDateChange,
    onTimeChange,
  }
}
